import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Bookmark, RefreshCw, Loader2, FileText, DollarSign, 
  Calendar, Clock, User, Mail, Download, ChevronRight
} from 'lucide-react';

const AdminSavedQuotes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchSavedQuotes();
  }, []);

  const fetchSavedQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/saved-quotes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSavedQuotes(response.data.saved_quotes || []);
    } catch (error) {
      console.error('Failed to fetch saved quotes:', error);
      toast.error('Failed to load saved quotes');
    }
    setLoading(false);
  };

  const viewQuote = (quote) => {
    setSelectedQuote(quote);
    setDialogOpen(true);
  };

  const downloadQuotePdf = async (savedQuoteId) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/quotes/saved/${savedQuoteId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${savedQuoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadFile = async (savedQuoteId, fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/quotes/saved/${savedQuoteId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `file_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'saved': return 'bg-blue-500/20 text-blue-400';
      case 'converted': return 'bg-green-500/20 text-green-400';
      case 'expired': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-saved-quotes">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-['Chivo'] font-black text-white">Saved Quotes</h1>
              <p className="text-white/50 mt-1">Customer quotes saved for later checkout</p>
            </div>
            <Button 
              onClick={fetchSavedQuotes}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
            </div>
          ) : savedQuotes.length === 0 ? (
            <div className="bg-[#111] border border-white/10 rounded-sm p-12 text-center">
              <Bookmark className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No saved quotes found</p>
            </div>
          ) : (
            <div className="bg-[#111] border border-white/10 rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Quote</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Customer</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Items</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Total</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Status</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Created</th>
                    <th className="text-left text-white/70 font-medium p-4 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedQuotes.map((quote) => (
                    <tr 
                      key={quote.saved_quote_id} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => viewQuote(quote)}
                    >
                      <td className="p-4">
                        <p className="text-white font-medium">{quote.name}</p>
                        <p className="text-white/40 text-xs font-mono">{quote.saved_quote_id}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-white">{quote.user_name || quote.customer_email}</p>
                        {quote.user_name && <p className="text-white/40 text-xs">{quote.user_email || quote.customer_email}</p>}
                      </td>
                      <td className="p-4">
                        <span className="text-white">{quote.items?.length || 0} item(s)</span>
                      </td>
                      <td className="p-4">
                        <span className="text-[#CCFF00] font-mono font-bold">${quote.total?.toFixed(2)}</span>
                      </td>
                      <td className="p-4">
                        <Badge className={`capitalize ${getStatusColor(quote.status)}`}>
                          {quote.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <p className="text-white/70 text-sm">
                          {new Date(quote.created_at).toLocaleDateString('en-AU')}
                        </p>
                        <p className="text-white/40 text-xs">
                          Expires: {new Date(quote.expires_at).toLocaleDateString('en-AU')}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); downloadQuotePdf(quote.saved_quote_id); }}
                            className="text-white/50 hover:text-[#CCFF00] hover:bg-white/5"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); viewQuote(quote); }}
                            className="text-white/50 hover:text-[#CCFF00] hover:bg-white/5"
                            title="View Details"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Quote Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Bookmark className="w-5 h-5 text-[#CCFF00]" />
              {selectedQuote?.name || 'Saved Quote'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#CCFF00]" />
                  Customer
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="text-white">{selectedQuote.user_name || 'Guest'}</p>
                  <p className="text-white/50 flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    {selectedQuote.user_email || selectedQuote.customer_email}
                  </p>
                </div>
              </div>

              {/* Quote Items */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#CCFF00]" />
                  Quote Items ({selectedQuote.items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedQuote.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{item.file_name}</p>
                          {item.file_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadFile(selectedQuote.saved_quote_id, item.file_id, item.file_name)}
                              className="h-6 px-2 text-white/50 hover:text-[#CCFF00] hover:bg-white/5"
                              title="Download file"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-white/50 text-sm">
                          {item.material && `${item.material} • `}
                          {item.volume_cm3 && `${item.volume_cm3?.toFixed(2)} cm³ • `}
                          Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-[#CCFF00] font-mono">${item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote Summary */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#CCFF00]" />
                  Price Summary
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Subtotal</span>
                    <span className="text-white">${selectedQuote.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${selectedQuote.delivery_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#CCFF00] font-bold font-mono">${selectedQuote.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Quote Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Saved: {new Date(selectedQuote.created_at).toLocaleDateString('en-AU')}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Expires: {new Date(selectedQuote.expires_at).toLocaleDateString('en-AU')}
                </div>
                <Badge className={`capitalize ${getStatusColor(selectedQuote.status)}`}>
                  {selectedQuote.status}
                </Badge>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={() => downloadQuotePdf(selectedQuote.saved_quote_id)}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default AdminSavedQuotes;
